import { formatRain, MM_PER_IN } from "./rainLayer.js";

// NOAA Atlas 14 point depths from the Precipitation Frequency Data Server.
// The browser requests same-origin /pfds (Vite in dev, CloudFront in production)
// because hdsc.nws.noaa.gov does not send Access-Control-Allow-Origin.

const MIN_MINUTES = 5;
const NAMED_YEARS = [1, 10, 100];
const cache = new Map();

const OUTSIDE = "No NOAA Atlas 14 estimate for this location.";
const UNAVAILABLE = "Frequency data unavailable";
const TOO_SHORT = "This period is shorter than the 5-minute Atlas 14 duration.";

function durationMinutes(label) {
  const m = String(label).match(/^(\d+(?:\.\d+)?)-(min|hr|day)$/i);
  if (!m) return null;
  const n = Number(m[1]);
  const unit = m[2].toLowerCase();
  if (unit === "min") return n;
  if (unit === "hr") return n * 60;
  if (unit === "day") return n * 1440;
  return null;
}

function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  if (minutes % 60 === 0) return `${(minutes / 60).toFixed(1)} h`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h} h ${m} min`;
}

function sameDepth(a, b) {
  return Math.abs(a - b) < 0.005;
}

// Partial-duration depths (inches) at an arbitrary duration. Exact table rows
// are used as-is. Other durations are log-interpolated in time and linear in
// depth. Nothing is extrapolated past the table.
export function depthsForMinutes(table, minutes) {
  const rows = table?.rows;
  if (!rows?.length || !(minutes >= MIN_MINUTES)) return null;
  const exact = rows.find((r) => r.minutes === minutes);
  if (exact) return { minutes, depths: exact.depths, interpolated: false };
  let lo = null;
  let hi = null;
  for (const row of rows) {
    if (row.minutes < minutes) lo = row;
    else if (row.minutes > minutes) {
      hi = row;
      break;
    }
  }
  if (!lo || !hi) return null;
  const t = (Math.log(minutes) - Math.log(lo.minutes)) / (Math.log(hi.minutes) - Math.log(lo.minutes));
  return {
    minutes,
    depths: lo.depths.map((d, i) => d + (hi.depths[i] - d) * t),
    interpolated: true,
    lo: lo.minutes,
    hi: hi.minutes,
  };
}

function bracketDepth(aris, depths, totalIn) {
  for (let i = 0; i < depths.length; i++) {
    if (sameDepth(totalIn, depths[i])) return { kind: "at", years: aris[i], inches: depths[i] };
  }
  if (totalIn < depths[0]) return { kind: "below", years: aris[0], inches: depths[0] };
  for (let i = 0; i < depths.length - 1; i++) {
    if (totalIn > depths[i] && totalIn < depths[i + 1]) {
      return {
        kind: "between",
        lowYears: aris[i],
        lowInches: depths[i],
        highYears: aris[i + 1],
        highInches: depths[i + 1],
      };
    }
  }
  const last = depths.length - 1;
  return { kind: "above", years: aris[last], inches: depths[last] };
}

export function parsePfds(text) {
  const body = String(text || "");
  if (/not within a project area/i.test(body) || /result\s*=\s*'none'/.test(body)) {
    return { ok: false, outside: true };
  }
  const lines = body.split(/\r?\n/);
  const volume = lines.find((l) => l.startsWith("NOAA Atlas"))?.trim() || "";
  const project = lines.find((l) => l.startsWith("Project area:"))?.replace(/^Project area:\s*/, "").trim() || "";
  const header = lines.findIndex((l) => l.includes("ARI (years)"));
  if (header < 0) return { ok: false, outside: false };
  const aris = lines[header].split(",").slice(1).map((s) => Number(s.trim())).filter((n) => Number.isFinite(n));
  if (!aris.length) return { ok: false, outside: false };
  const rows = [];
  for (const line of lines.slice(header + 1)) {
    const m = line.match(/^(\d+(?:\.\d+)?-(?:min|hr|day)):,\s*(.*)$/i);
    if (!m) {
      if (rows.length) break;
      continue;
    }
    const minutes = durationMinutes(m[1]);
    const depths = m[2].split(",").map((s) => Number(s.trim()));
    if (minutes == null || depths.length !== aris.length || depths.some((d) => !Number.isFinite(d))) continue;
    rows.push({ label: m[1], minutes, depths });
  }
  if (!rows.length) return { ok: false, outside: false };
  return { ok: true, outside: false, volume, project, aris, rows };
}

// One sentence for the rain chart. Depths are inches in `table`; `totalMm` is
// the radar accumulation over the selected window.
export function formatPfdsLine(table, { totalMm, durationMs, units = "in" } = {}) {
  if (!table?.ok) return table?.outside ? OUTSIDE : UNAVAILABLE;
  const minutes = Math.round(durationMs / 60000);
  if (!(minutes >= MIN_MINUTES)) return TOO_SHORT;
  const at = depthsForMinutes(table, minutes);
  if (!at) return UNAVAILABLE;
  const totalIn = totalMm / MM_PER_IN;
  const bracket = bracketDepth(table.aris, at.depths, totalIn);
  const depthLabel = (inches) => formatRain(inches * MM_PER_IN, units);
  let clause;
  if (bracket.kind === "below") clause = `is below the ${bracket.years}-year (${depthLabel(bracket.inches)})`;
  else if (bracket.kind === "above") clause = `is above the ${bracket.years}-year (${depthLabel(bracket.inches)})`;
  else if (bracket.kind === "at") clause = `is at the ${bracket.years}-year (${depthLabel(bracket.inches)})`;
  else clause = `is above the ${bracket.lowYears}-year (${depthLabel(bracket.lowInches)}) and below the ${bracket.highYears}-year (${depthLabel(bracket.highInches)})`;
  const interp = at.interpolated ? ` (interpolated between ${formatDuration(at.lo)} and ${formatDuration(at.hi)})` : "";
  const named = NAMED_YEARS.map((y) => {
    const i = table.aris.indexOf(y);
    return i < 0 ? null : `${y}-year ${depthLabel(at.depths[i])}`;
  }).filter(Boolean).join(" · ");
  return `Radar ${formatRain(totalMm, units)} over ${formatDuration(minutes)}${interp} ${clause}. ${named}.`;
}

export function clearPfdsCache() {
  cache.clear();
}

export async function fetchPfds(lat, lon, { signal, fetchImpl = fetch } = {}) {
  const latQ = Number(lat).toFixed(3);
  const lonQ = Number(lon).toFixed(3);
  const key = `${latQ},${lonQ}`;
  if (cache.has(key)) return cache.get(key);
  const q = new URLSearchParams({
    lat: latQ,
    lon: lonQ,
    data: "depth",
    units: "english",
    series: "pds",
  });
  // The request itself is not aborted: a closed chart should still fill the
  // cache for the next pin at this point. Callers ignore the result via signal.
  const pending = (async () => {
    const resp = await fetchImpl(`/pfds?${q}`);
    if (!resp.ok) throw new Error(`PFDS request failed (${resp.status})`);
    const table = parsePfds(await resp.text());
    if (!table.ok && !table.outside) throw new Error(UNAVAILABLE);
    return table;
  })();
  cache.set(key, pending);
  try {
    const table = await pending;
    if (signal?.aborted) {
      const err = new Error("The operation was aborted");
      err.name = "AbortError";
      throw err;
    }
    return table;
  } catch (err) {
    if (err?.name !== "AbortError") cache.delete(key);
    throw err;
  }
}
